/**
 * CustomerDefaultOptIn -- the UNCHECKED "Also apply the customer's onboarding default" box shown
 * next to every Move (device page Placement, Devices bulk Assign, Onboard "In other sites").
 *
 * OP#1935 (2026-10-19): a move used to re-apply the target customer's onboarding default
 * silently (rename, tags, queued firmware -- even a downgrade). The server now applies it to an
 * already-placed device ONLY when the body carries `apply_customer_default: true`, which is what
 * ticking this box sends. The box lists exactly what it would change, read from the TARGET
 * customer's saved default (GET /orgs/<id>/onboarding-profile). When that customer has no
 * default worth applying, a plain note replaces the box (nothing to opt in to).
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { customerDefaultChanges, type OnboardingDefault } from "@/lib/deviceMove";

export function CustomerDefaultOptIn({
  orgId,
  checked,
  onChange,
  testId = "customer-default-optin",
}: {
  /** The TARGET site's org (the customer whose default would be applied). */
  orgId: string | number | null | undefined;
  checked: boolean;
  onChange: (next: boolean) => void;
  testId?: string;
}) {
  // Same query key as the Onboard screen's profile form, so a saved edit refreshes both.
  const profile = useQuery({
    queryKey: ["v2", "onboarding-profile", orgId == null ? null : String(orgId)],
    queryFn: () => api.get<OnboardingDefault>(`/orgs/${orgId}/onboarding-profile`),
    enabled: orgId != null && orgId !== "",
  });
  if (orgId == null || orgId === "") return null;
  const changes = customerDefaultChanges(profile.data);
  if (profile.isLoading) return null;
  if (changes.length === 0) {
    return (
      <p className="mt-1 text-xs text-ink-subtle" data-testid={testId}>
        Only the placement changes: name, tags and firmware stay as they are.
      </p>
    );
  }
  return (
    <div className="mt-2 rounded border border-line bg-surface-2 p-2 text-xs" data-testid={testId}>
      <label className="flex items-start gap-2 text-ink">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>
          Also apply the customer&apos;s onboarding default to devices that are already placed
          (off by default: a move changes only the placement). This would:
        </span>
      </label>
      <ul className="ml-6 mt-1 list-disc text-ink-muted">
        {changes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
    </div>
  );
}
